import React, { useState } from 'react';
import axios from 'axios';
import PhoneInputComponent from "./phone_input_comp";
import { isValidPhoneNumber } from "libphonenumber-js";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import "./ModalStyles.css";
import { VALIDATE_ADDRESS_ENDPOINT, APPOINTMENT_ENDPOINT } from './config';

const AppointmentForm = ({ isModal = false }) => {
  const [newAppointment, setNewAppointment] = useState({
    name: '',
    email: '',
    address: '',
    zip_code: '',
    number: '+1',
    appliance: '',
    date: '',
    time_window: '8am-11am',
    problem: ''
  });
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isVerifyingAddress, setIsVerifyingAddress] = useState(false);

  const zipCodeRegex = /^\d{5}(-\d{4})?$/;
  const isValidZip = (zip) => zipCodeRegex.test(zip);

  const handleChange = (event) => {
    if (typeof event === "string") {
      setNewAppointment(prev => ({ ...prev, number: event }));
    } else {
      const { name, value } = event.target;
      setNewAppointment(prev => ({ ...prev, [name]: value }));
    }
  };

  const verifyAddress = async () => {
    setIsVerifyingAddress(true);
    setErrors((prevErrors) => ({ ...prevErrors, address: "", zip_code: "" }));

    try {
      const response = await axios.post(VALIDATE_ADDRESS_ENDPOINT, {
        street: newAppointment.address,
        city: null,
        state: null,
        zip: newAppointment.zip_code,
        zipPlus4: null,
        secondary: null
      });

      const verified = response.data.address;

      // Optionally update form with standardized address
      setNewAppointment((prev) => ({
        ...prev,
        address: verified.streetAddress || prev.address,
        zip_code: verified.ZIPCode || prev.zip_code,
      }));

      setIsVerifyingAddress(false);
      return true;
    } catch (error) {
      console.error("Address verification failed:", error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        address: "USPS could not validate this address.",
        zip_code: "ZIP Code might not match the address."
      }));
      setIsVerifyingAddress(false);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let formErrors = {};

    if (!isValidPhoneNumber(newAppointment.number, "US")) {
      formErrors.number = "Phone Number is invalid.";
    }
    if (!isValidZip(newAppointment.zip_code)) {
      formErrors.zip_code = "Zip Code is invalid.";
    }
    if (!newAppointment.name.trim()) {
      formErrors.name = "Full Name is required.";
    }
    if (!newAppointment.email.trim()) {
      formErrors.email = "Email Address is required.";
    } else if (!/\S+@\S+\.\S+/.test(newAppointment.email)) {
      formErrors.email = "Enter a valid email address.";
    }
    if (!newAppointment.address.trim()) {
      formErrors.address = "Address is required.";
    }
    if (!newAppointment.appliance) {
      formErrors.appliance = "Please select an appliance.";
    }
    if (!newAppointment.date) {
      formErrors.date = "Date is required.";
    }
    if (!newAppointment.time_window) {
      formErrors.time_window = "Please select an appointment window.";
    }
    if (!newAppointment.problem.trim()) {
      formErrors.problem = "Description of the problem is required.";
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    const isAddressValid = await verifyAddress();
    if (!isAddressValid) {
      console.warn("Submission stopped: Invalid address.");
      return;
    }

    try {
      const response = await axios.post(APPOINTMENT_ENDPOINT, newAppointment);
      console.log('Appointment added:', response.data);

      setNewAppointment({
        name: '',
        email: '',
        address: '',
        zip_code: '',
        number: '+1',
        appliance: '',
        date: '',
        time_window: '8am-11am',
        problem: ''
      });
      setErrors({});
      setShowSuccessModal(true);

    } catch (error) {
      console.error("There was an error adding the appointment!", error);
    }
  };

  const handleAddressChange = (addressValue) => {
    setNewAppointment((prev) => ({ ...prev, address: addressValue }));
  };

  const handleAddressSelect = async (selected) => {
    setNewAppointment((prev) => ({ ...prev, address: selected }));
    try {
      const results = await geocodeByAddress(selected);
      if (results[0]) {
        const latLng = await getLatLng(results[0]);
        console.log("Coordinates are:", latLng);

        const zipComponent = results[0].address_components.find(comp => comp.types.includes("postal_code"));
        if (zipComponent) {
          setNewAppointment(prev => ({ ...prev, zip_code: zipComponent.long_name }));
        }
      }
    } catch (error) {
      console.error("Error in handleAddressSelect:", error);
    }
  };

  return (
    <div className="page-container">
      <div id="schedule-appointment-section" className={`form-container ${isModal ? 'modal-form' : ''}`}>
        <h1 className="title">REQUEST REPAIR</h1>
        <form className="appointment-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={newAppointment.name}
              onChange={handleChange}
              className={`input-field ${errors.name ? "error-input" : ""}`}
            />
            {errors.name && <div className="error-text">{errors.name}</div>}
          </div>

          {/* Email */}
          <div className="form-group">
            <input
              type="text"
              name="email"
              placeholder="Email Address"
              value={newAppointment.email}
              onChange={handleChange}
              className={`input-field ${errors.email ? "error-input" : ""}`}
            />
            {errors.email && <div className="error-text">{errors.email}</div>}
          </div>

          {/* Address */}
          <div className="form-group autocomplete-wrapper">
            <PlacesAutocomplete
              value={newAppointment.address}
              onChange={handleAddressChange}
              onSelect={handleAddressSelect}
              searchOptions={{
                componentRestrictions: { country: ["us"] },
                // optional: restrict to the US
              }}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div>
                  <input
                    {...getInputProps({
                      name: "address",
                      placeholder: "Address",
                      className: `input-field`,
                    })}
                  />

                  {/* Only render dropdown if we have suggestions or loading */}
                  {(loading || suggestions.length > 0) && (
                    <div className="autocomplete-dropdown">
                      {loading && <div>Loading...</div>}
                      {suggestions.map((suggestion) => {
                        const style = {
                          backgroundColor: suggestion.active ? "#fafafa" : "#ffffff",
                          padding: "8px",
                          cursor: "pointer",
                        };
                        return (
                          <div
                            key={suggestion.placeId}
                            {...getSuggestionItemProps(suggestion, { style })}
                          >
                            {suggestion.description}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </PlacesAutocomplete>
          </div>


          {/* Zip Code */}
          <div className="form-group">
            <input
              type="text"
              name="zip_code"
              placeholder="Zip Code"
              value={newAppointment.zip_code}
              onChange={handleChange}
              className={`input-field ${errors.zip_code ? "error-input" : ""}`}
            />
            {errors.zip_code && (
              <div className="error-text">{errors.zip_code}</div>
            )}
          </div>

          {/* Phone Number */}
          <PhoneInputComponent
            value={newAppointment.number}
            onChange={handleChange}
            errorMessage={errors.number}
          />

          {/* Appliance Dropdown */}
          <div className="form-group">
            <select
              name="appliance"
              value={newAppointment.appliance}
              onChange={handleChange}
              className={`select-field ${errors.appliance ? "error-input" : ""}`}
              style={{ color: newAppointment.appliance ? "black" : "gray" }}
            >
              <option value="" disabled hidden>Select an appliance</option>
              <option value="Fridge/Refrigerator/Freezer/Ice Maker (Sub-Zero)">Fridge/Refrigerator/Freezer/Ice Maker (Sub-Zero)</option>
              <option value="Wine Cooler (Sub-Zero)">Wine Cooler (Sub-Zero)</option>
              <option value="Range/Oven/Stove/Cooktop (Wolf)">Range/Oven/Stove/Cooktop (Wolf)</option>
              <option value="Ventilations (Wolf)">Ventilations (Wolf)</option>
              <option value="Dishwasher (Cove)">Dishwasher (Cove)</option>
              <option value="Other">Other</option>
            </select>
            {errors.appliance && <div className="error-text">{errors.appliance}</div>}
          </div>

          {/* Date */}
          <div className="form-group">
            <label>Appointment Date</label>
            <input
              type="date"
              name="date"
              value={newAppointment.date}
              onChange={handleChange}
              className={`input-field ${errors.date ? "error-input" : ""}`}
            />
            {errors.date && <div className="error-text">{errors.date}</div>}
          </div>

          {/* Appointment Window (Radio) */}
          <div className="form-group">
            <label className="appointment-window-title">Appointment Window</label>
            <div className="radio-group">
              {["8am-11am", "9am-12pm", "12pm-3pm", "3pm-6pm", "4pm-7pm"].map((windowVal) => (
                <label key={windowVal} className="radio-item">
                  <input
                    type="radio"
                    name="time_window"
                    value={windowVal}
                    checked={newAppointment.time_window === windowVal}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <span className="radio-label">{windowVal}</span>
                </label>
              ))}
            </div>
            {errors.time_window && (
              <div className="error-text">{errors.time_window}</div>
            )}
          </div>

          {/* Problem Description */}
          <div className="form-group">
            <textarea
              name="problem"
              placeholder="Describe the problem"
              value={newAppointment.problem}
              onChange={handleChange}
              className={`textarea-field ${errors.problem ? "error-input" : ""}`}
            />
            {errors.problem && (
              <div className="error-text">{errors.problem}</div>
            )}
          </div>

          {/* <button type="submit" className="submit-btn" onClick={verifyAddress}>Submit Request</button> */}
          {/* <button type="submit" className="submit-btn" disabled={isVerifyingAddress}> */}
          <button type="submit" className="submit-btn">Submit Request</button>
          {/* {isVerifyingAddress ? "Verifying Address..." : "Submit Request"} */}
        </form>
        {showSuccessModal && (
          <SuccessModal onClose={() => setShowSuccessModal(false)} />
        )}
      </div>
    </div>
  );
};

/* Minimal success modal after submission */
function SuccessModal({ onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Success</h2>
        <p>Your appointment request has been submitted!</p>
        <button onClick={onClose}>OK</button>
      </div>
    </div>
  );
}

export default AppointmentForm;